import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell, inputCls, Field } from "@/components/AdminTools";
import {
  PackagePlus, Sparkles, Wand2, RefreshCw, ShieldCheck, MapPin, Calendar,
  AlertCircle, ChevronRight, Save,
} from "lucide-react";

const STATUS_TONE = {
  open: "bg-blue-100 text-blue-700",
  sourcing: "bg-amber-100 text-amber-700",
  matched: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
};

const STATUSES = ["open", "sourcing", "matched", "closed"];

/**
 * Admin triage surface for clinic-submitted "Request services not listed".
 * One-click AI vendor matching via Claude — caches result on the document.
 */
export default function AdminServiceRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [matching, setMatching] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/admin/service-requests")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const triggerMatch = async (id) => {
    setMatching((m) => ({ ...m, [id]: true }));
    try {
      const r = await api.post(`/admin/service-requests/${id}/ai-match`);
      setItems((rows) => rows.map((it) => (it.id === id ? { ...it, ai_match: r.data } : it)));
      toast.success(
        r.data.used_llm
          ? `AI ranked ${r.data.matches.length} vendor${r.data.matches.length === 1 ? "" : "s"}.`
          : `Heuristic match returned ${r.data.matches.length} vendor${r.data.matches.length === 1 ? "" : "s"} (LLM unavailable).`
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMatching((m) => ({ ...m, [id]: false }));
    }
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Concierge"
      title="Service requests"
      intro="Clinic-submitted requests for services not in the catalog yet. Triage status, leave concierge notes, and use AI matching to rank network vendors against each request."
      actions={
        <button
          type="button"
          onClick={load}
          data-testid="admin-service-requests-refresh"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold hover:bg-slate-50"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      }
    >
      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <PackagePlus className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold text-slate-900">No service requests yet</div>
          <p className="text-sm text-slate-500 mt-1">Clinic-submitted requests will appear here for triage.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-service-requests-list">
          {items.map((it) => (
            <Row
              key={it.id}
              item={it}
              expanded={activeId === it.id}
              onToggle={() => setActiveId(activeId === it.id ? null : it.id)}
              onMatch={() => triggerMatch(it.id)}
              matching={!!matching[it.id]}
              onUpdated={(updated) => setItems((rows) => rows.map((r) => (r.id === updated.id ? updated : r)))}
            />
          ))}
        </ul>
      )}
    </ToolPageShell>
  );
}

function Row({ item: it, expanded, onToggle, onMatch, matching, onUpdated }) {
  const [status, setStatus] = useState(it.status);
  const [notes, setNotes] = useState(it.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/service-requests/${it.id}`, {
        status, admin_notes: notes,
      });
      onUpdated(r.data);
      if (r.data.auto_bookmarked_vendor_id) {
        toast.success("Saved · top AI match auto-bookmarked for the clinic.");
      } else {
        toast.success("Saved.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const matches = it.ai_match?.matches || [];

  return (
    <li
      data-testid={`admin-service-request-${it.id}`}
      className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`admin-service-request-toggle-${it.id}`}
        className="w-full p-5 flex items-start gap-3 text-left hover:bg-slate-50 transition"
      >
        <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 border border-blue-100 flex items-center justify-center shrink-0">
          <PackagePlus className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-base font-semibold text-slate-900">{it.title}</h3>
            <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${STATUS_TONE[it.status] || "bg-slate-100 text-slate-600"}`}>
              {it.status}
            </span>
            {it.priority === "urgent" && (
              <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-rose-100 text-rose-700 inline-flex items-center gap-0.5">
                <AlertCircle className="w-2.5 h-2.5" /> Urgent
              </span>
            )}
            {it.ai_match && (
              <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-violet-100 text-violet-700 inline-flex items-center gap-0.5">
                <Sparkles className="w-2.5 h-2.5" /> {matches.length} AI match{matches.length === 1 ? "" : "es"}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[11px] text-slate-500">
            {it.clinic_name || "—"} · submitted {new Date(it.created_at).toLocaleDateString()}
          </div>
          <p className="mt-2 text-sm text-slate-700 line-clamp-2">{it.description}</p>
          <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
            {it.category && <Tag>{it.category}</Tag>}
            {it.target_volume_per_month != null && <Tag>{it.target_volume_per_month}/mo</Tag>}
            {it.target_unit_price != null && <Tag>${it.target_unit_price}</Tag>}
            {it.geography && <Tag><MapPin className="w-2.5 h-2.5 inline mr-0.5" /> {it.geography}</Tag>}
            {it.needed_by && <Tag><Calendar className="w-2.5 h-2.5 inline mr-0.5" /> {it.needed_by}</Tag>}
          </div>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 mt-2 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/60 p-5 space-y-4" data-testid={`admin-service-request-detail-${it.id}`}>
          {/* Triage controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Status">
              <select
                data-testid={`admin-service-request-status-${it.id}`}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className={inputCls}
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Concierge notes (visible to clinic)">
                <textarea
                  rows={2}
                  data-testid={`admin-service-request-notes-${it.id}`}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Sourced 3 vendors, intro emails out Wednesday."
                  className={inputCls}
                />
              </Field>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              data-testid={`admin-service-request-save-${it.id}`}
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-1.5 text-xs font-semibold hover:bg-slate-800 disabled:opacity-60"
            >
              <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save triage"}
            </button>
          </div>

          {/* AI match block */}
          <div className="rounded-2xl bg-white border border-violet-200 p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-violet-700 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> AI vendor match
              </div>
              <button
                type="button"
                data-testid={`admin-service-request-ai-match-${it.id}`}
                onClick={onMatch}
                disabled={matching}
                className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-violet-700 disabled:opacity-60"
              >
                <Wand2 className="w-3 h-3" />
                {matching ? "Ranking…" : it.ai_match ? "Re-rank" : "Run AI match"}
              </button>
            </div>

            {!it.ai_match ? (
              <p className="mt-2 text-xs text-slate-500">
                Click "Run AI match" to have Claude rank up to 5 vendors from the active network against this request — score (0-100) + one-line operational fit rationale.
              </p>
            ) : matches.length === 0 ? (
              <p className="mt-2 text-xs text-slate-500">No vendor in the current network is a reasonable match. Source externally and apply via the standard onboarding pipeline.</p>
            ) : (
              <ul className="mt-3 space-y-2" data-testid={`admin-service-request-matches-${it.id}`}>
                {matches.map((m, i) => (
                  <li
                    key={m.vendor_id}
                    data-testid={`admin-service-request-match-${i}`}
                    className="rounded-xl border border-slate-200 bg-white p-3 flex items-start gap-3"
                  >
                    <span className="w-8 h-8 rounded-lg bg-violet-50 text-violet-700 border border-violet-100 flex items-center justify-center text-[10px] font-bold shrink-0">
                      #{i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 text-sm">{m.vendor_name}</span>
                        <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-slate-100 text-slate-700">
                          {m.category}{m.subcategory ? ` · ${m.subcategory}` : ""}
                        </span>
                        <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-violet-700">
                          <ShieldCheck className="w-3 h-3" /> {m.score}/100
                        </span>
                      </div>
                      {m.rationale && (
                        <p className="mt-1 text-[12px] text-slate-700 leading-relaxed">{m.rationale}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {it.ai_match?.generated_at && (
              <div className="mt-2 text-[10px] text-slate-400">
                {it.ai_match.used_llm ? "Claude · " : "Heuristic fallback · "}
                generated {new Date(it.ai_match.generated_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function Tag({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 capitalize">
      {children}
    </span>
  );
}
